import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLANS = [
  {
    id: "free",
    name: "Rookie",
    price: { monthly: 0, yearly: 0 },
    tagline: "Get a taste",
    features: [
      { text: "Profile setup", included: true },
      { text: "1 sample gym workout", included: true },
      { text: "1 sample meal", included: true },
      { text: "1 goal", included: true },
      { text: "2 recovery tips", included: true },
      { text: "Full AI-generated plans", included: false },
      { text: "Coach access", included: false },
    ],
    cta: "Current",
  },
  {
    id: "pro",
    name: "Pro",
    price: { monthly: 9.99, yearly: 95.90 },
    tagline: "Serious training",
    highlight: true,
    features: [
      { text: "Everything in Rookie", included: true },
      { text: "AI-generated gym plans", included: true },
      { text: "AI-generated meal plans", included: true },
      { text: "Unlimited goals", included: true },
      { text: "All recovery protocols", included: true },
      { text: "Progress tracking", included: true },
      { text: "Coach access", included: false },
    ],
    cta: "Go Pro",
    lookup: { monthly: "pro_monthly", yearly: "pro_yearly" },
  },
  {
    id: "elite",
    name: "Elite",
    price: { monthly: 19.99, yearly: 191.90 },
    tagline: "Coach in your corner",
    features: [
      { text: "Everything in Pro", included: true },
      { text: "Link with your coach", included: true },
      { text: "Coach sees your plans & progress", included: true },
      { text: "Direct coach notes", included: true },
      { text: "Priority AI regeneration", included: true },
    ],
    cta: "Go Elite",
    lookup: { monthly: "elite_monthly", yearly: "elite_yearly" },
  },
];

const COACH_PLAN = {
  id: "coach_pro",
  name: "Coach Pro",
  price: { monthly: 29.99, yearly: 287.90 },
  tagline: "Manage your squad",
  features: [
    "View all connected players' profiles",
    "See gym & meal plans",
    "Track their progress & goals",
    "Leave direct guidance notes",
    "Multiple players, one dashboard",
  ],
  lookup: { monthly: "coach_monthly", yearly: "coach_yearly" },
};

const TEAM_PLAN = {
  id: "team",
  name: "Team",
  price: { monthly: 99, yearly: 950 },
  tagline: "One licence, whole squad",
  features: [
    "1 coach + 14 player seats",
    "All players get Team-tier access (full AI plans, unlimited goals, all recovery)",
    "Coach dashboard for every player",
    "Team code invites — 1-click join",
    "Match-week auto-taper for the whole squad",
  ],
  lookup: { monthly: "team_monthly", yearly: "team_yearly" },
};

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cycle, setCycle] = useState("monthly");
  const [loading, setLoading] = useState(null);

  const checkout = async (lookup_key) => {
    if (!user) { nav("/login"); return; }
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(null);
    }
  };

  const isCoach = user?.role === "coach";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body py-16 px-6">
      <div className="max-w-7xl mx-auto">
        <Link to={user ? (user.role === "coach" ? "/coach" : "/dashboard") : "/"}
          className="inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest text-[#A3A3A3] hover:text-white mb-8" data-testid="back-link">
          <ArrowLeft size={16} /> Back
        </Link>
        <div className="text-center mb-12">
          <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Choose Your Path</div>
          <h1 className="font-heading font-black uppercase text-6xl md:text-7xl mt-2">Pricing</h1>
          <p className="text-[#A3A3A3] mt-4">Cancel anytime. Annual saves ~20%.</p>
        </div>

        {/* Cycle toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex border border-[#262626]">
            <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
              className={`px-6 py-3 font-heading uppercase tracking-widest text-sm ${cycle === "monthly" ? "bg-[#FF3B30] text-white" : "text-[#A3A3A3] hover:text-white"}`}>
              Monthly
            </button>
            <button onClick={() => setCycle("yearly")} data-testid="cycle-yearly"
              className={`px-6 py-3 font-heading uppercase tracking-widest text-sm ${cycle === "yearly" ? "bg-[#FF3B30] text-white" : "text-[#A3A3A3] hover:text-white"}`}>
              Yearly <span className="text-[10px] ml-1 opacity-70">-20%</span>
            </button>
          </div>
        </div>

        {!isCoach && (
          <div className="grid md:grid-cols-3 gap-4">
            {PLANS.map((p) => (
              <div key={p.id} data-testid={`plan-${p.id}`}
                className={`border p-8 flex flex-col ${p.highlight ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-[#262626] bg-[#121212]"}`}>
                {p.highlight && (
                  <div className="inline-block border border-[#FF3B30] bg-[#FF3B30] text-white px-2 py-0.5 text-xs font-heading uppercase tracking-widest mb-4 self-start">
                    Most Popular
                  </div>
                )}
                <div className="font-heading font-black uppercase text-3xl">{p.name}</div>
                <div className="text-sm text-[#A3A3A3] mt-1">{p.tagline}</div>
                <div className="mt-6 mb-6">
                  <span className="font-heading font-black text-6xl">${p.price[cycle]}</span>
                  <span className="text-sm text-[#A3A3A3] ml-2">/ {cycle === "monthly" ? "mo" : "yr"}</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check size={18} weight="bold" className={f.included ? "text-[#FF3B30] mt-0.5 flex-shrink-0" : "text-[#262626] mt-0.5 flex-shrink-0"} />
                      <span className={f.included ? "" : "text-[#A3A3A3] line-through"}>{f.text}</span>
                    </li>
                  ))}
                </ul>
                {p.id === "free" ? (
                  <Button disabled className="rounded-none bg-[#262626] text-[#A3A3A3] font-heading uppercase tracking-widest font-bold h-11" data-testid={`cta-${p.id}`}>
                    {user?.subscription_tier === "free" ? "Current Plan" : "Free"}
                  </Button>
                ) : user?.subscription_tier === p.id ? (
                  <Button disabled className="rounded-none bg-[#34C759] text-white font-heading uppercase tracking-widest font-bold h-11" data-testid={`cta-${p.id}`}>
                    Active
                  </Button>
                ) : (
                  <Button onClick={() => checkout(p.lookup[cycle])} disabled={loading === p.lookup[cycle]}
                    data-testid={`cta-${p.id}`}
                    className={`rounded-none font-heading uppercase tracking-widest font-bold h-11 ${p.highlight ? "bg-[#FF3B30] hover:bg-[#D63026]" : "bg-white text-black hover:bg-[#A3A3A3]"}`}>
                    {loading === p.lookup[cycle] ? "…" : p.cta}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {isCoach && (
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-4">
            <div className="border border-[#262626] bg-[#121212] p-8" data-testid="plan-coach">
              <div className="inline-block border border-[#262626] px-2 py-0.5 text-xs font-heading uppercase tracking-widest mb-4 text-[#A3A3A3]">Solo Coach</div>
              <div className="font-heading font-black uppercase text-3xl">{COACH_PLAN.name}</div>
              <div className="text-sm text-[#A3A3A3] mt-1">{COACH_PLAN.tagline}</div>
              <div className="mt-6 mb-6">
                <span className="font-heading font-black text-5xl">${COACH_PLAN.price[cycle]}</span>
                <span className="text-sm text-[#A3A3A3] ml-2">/ {cycle === "monthly" ? "mo" : "yr"}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {COACH_PLAN.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check size={18} weight="bold" className="text-[#FF3B30] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {user?.subscription_tier === "coach_pro" ? (
                <Button disabled className="w-full rounded-none bg-[#34C759] text-white font-heading uppercase tracking-widest font-bold h-11" data-testid="cta-coach">Active</Button>
              ) : (
                <Button onClick={() => checkout(COACH_PLAN.lookup[cycle])} disabled={loading}
                  data-testid="cta-coach"
                  className="w-full rounded-none bg-white text-black hover:bg-[#A3A3A3] font-heading uppercase tracking-widest font-bold h-11">
                  {loading ? "…" : "Subscribe"}
                </Button>
              )}
            </div>

            <div className="border border-[#FF3B30] bg-[#FF3B30]/5 p-8" data-testid="plan-team">
              <div className="inline-block border border-[#FF3B30] bg-[#FF3B30] text-white px-2 py-0.5 text-xs font-heading uppercase tracking-widest mb-4">Best Value</div>
              <div className="font-heading font-black uppercase text-3xl">{TEAM_PLAN.name}</div>
              <div className="text-sm text-[#A3A3A3] mt-1">{TEAM_PLAN.tagline}</div>
              <div className="mt-6 mb-6">
                <span className="font-heading font-black text-5xl">${TEAM_PLAN.price[cycle]}</span>
                <span className="text-sm text-[#A3A3A3] ml-2">/ {cycle === "monthly" ? "mo" : "yr"}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {TEAM_PLAN.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check size={18} weight="bold" className="text-[#FF3B30] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {user?.subscription_tier === "team" ? (
                <Button disabled className="w-full rounded-none bg-[#34C759] text-white font-heading uppercase tracking-widest font-bold h-11" data-testid="cta-team">Active</Button>
              ) : (
                <Button onClick={() => checkout(TEAM_PLAN.lookup[cycle])} disabled={loading}
                  data-testid="cta-team"
                  className="w-full rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11">
                  {loading ? "…" : "Subscribe"}
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="mt-16 text-center text-xs text-[#A3A3A3] max-w-2xl mx-auto">
          Test card: <span className="text-white font-mono">4242 4242 4242 4242</span> · any future expiry · any CVC.
          <br />Stripe manages everything including tax and compliance handling (+3.5% per transaction).
        </div>
      </div>
    </div>
  );
}
